import React, { useRef, useState, useEffect } from 'react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import SignatureCanvas from 'react-signature-canvas';
import { useToast } from '../../../hooks/useToast';
import api from '../../../services/api';

const ApproveTicketModal = ({ isOpen, onClose, onSubmit, ticket }) => {
  const [feedbackRating, setFeedbackRating] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const [workReport, setWorkReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const signaturePadRef = useRef(null);
  const { success, error } = useToast();

  useEffect(() => {
    if (isOpen && ticket?.id) {
      const fetchWorkReport = async () => {
        setLoadingReport(true);
        try {
          const response = await api.get(`/api/v1/work-reports/?ticket_id=${ticket.id}`);
          if (response.data && response.data.length > 0) {
            const sortedReports = response.data.sort((a, b) => b.id - a.id);
            setWorkReport(sortedReports[0]);
          } else {
            setWorkReport(null);
          }
        } catch (err) {
          console.error("Failed to fetch work report for approval", err);
        } finally {
          setLoadingReport(false);
        }
      };
      fetchWorkReport();
    }
  }, [isOpen, ticket]);

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
    setSignatureData(null);
  };

  const saveSignature = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const data = signaturePadRef.current.toDataURL();
      setSignatureData(data);
      success('Signature saved!');
      return data;
    }
    error('Please provide your signature');
    return null;
  };

  const handleConfirm = () => {
    if (!feedbackRating) {
      error('Please select a feedback rating out of 10');
      return;
    }

    if (!feedbackText.trim()) {
      error('Please enter your feedback');
      return;
    }

    const signature = signatureData || saveSignature();
    if (!signature) {
      return;
    }

    onSubmit(ticket.id, {
      customer_signature: signature,
      customer_feedback_rating: Number(feedbackRating),
      customer_feedback_text: feedbackText.trim()
    });
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = event.target.result;
      setSignatureData(data);
      success('Signature image uploaded!');
      
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleClose = () => {
    clearSignature();
    setFeedbackRating('');
    setFeedbackText('');
    setWorkReport(null);
    onClose();
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose}>Cancel</Button>
      <Button variant="primary" onClick={handleConfirm}>Approve Ticket</Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Approve Ticket #${ticket?.id || ''}`}
      size="medium"
      footer={footer}
    >
      <div style={{ marginBottom: '16px', color: 'var(--text-secondary)', fontSize: '14px' }}>
        Please sign below or upload a signature image to approve this ticket.
      </div>

      {loadingReport ? (
        <div style={{ marginBottom: '20px', padding: '12px', background: 'var(--surface-light)', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center' }}>
          Loading engineer work report...
        </div>
      ) : workReport ? (
        <div style={{ 
          marginBottom: '20px', 
          padding: '16px', 
          background: 'var(--surface-light)', 
          borderRadius: '8px', 
          border: '1px solid var(--border)' 
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
            Engineer's Work Report Summary
          </h4>
          
          <div style={{ marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block' }}>
              Action Taken:
            </span>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
              {workReport.action_taken || 'N/A'}
            </p>
          </div>
          
          {workReport.observations && (
            <div>
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block' }}>
                Observations:
              </span>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                {workReport.observations}
              </p>
            </div>
          )}
        </div>
      ) : null}
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px', display: 'block' }}>
          Feedback Rating (Out of 10)
        </label>
        <select 
          value={feedbackRating} 
          onChange={(e) => setFeedbackRating(e.target.value)} 
          style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--background)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit' }}
        >
          <option value="">Select rating</option>
          {[...Array(10)].map((_, idx) => {
            const value = String(idx + 1);
            return <option key={value} value={value}>{value}</option>;
          })}
        </select>
      </div>
      
      <div style={{ marginBottom: '16px' }}>
        <label style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px', display: 'block' }}>
          Feedback
        </label>
        <textarea 
          value={feedbackText} 
          onChange={(e) => setFeedbackText(e.target.value)} 
          placeholder="Share your feedback on the service experience..." 
          style={{ width: '100%', minHeight: '120px', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--background)', color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }} 
        />
      </div>
      
      <div style={{ width: '100%', borderRadius: '8px', border: '1px solid var(--border)', background: '#f9fafb', overflow: 'hidden' }}>
        <SignatureCanvas 
          ref={signaturePadRef} 
          onBegin={() => {
            if (signatureData) setSignatureData(null);
          }}
          canvasProps={{ style: { width: '100%', height: '200px', touchAction: 'none', cursor: 'crosshair' } }} 
          penColor="#000" 
          velocityFilterWeight={0.7} 
          minWidth={0.5} 
          maxWidth={2.5} 
        />
      </div>
      
      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
        <Button variant="primary" onClick={saveSignature}>Save Signature</Button>
        <Button variant="secondary" onClick={clearSignature}>Clear Signature</Button>
        <input
          type="file"
          accept="image/*"
          onChange={handleSignatureUpload}
          style={{ display: 'none' }}
          id="customer-signature-upload"
        />
        <Button variant="secondary" onClick={() => document.getElementById('customer-signature-upload').click()}>
          Upload Image
        </Button>
      </div>
      
      {signatureData && (
        <div style={{ 
          marginTop: '12px', 
          padding: '12px', 
          border: '1px solid rgba(16, 185, 129, 0.2)', 
          borderRadius: '8px', 
          background: 'rgba(16, 185, 129, 0.05)' 
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#10b981', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>✓</span> Current Saved Signature:
          </div>
          <img 
            src={signatureData} 
            alt="Current Signature" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '120px', 
              height: 'auto', 
              borderRadius: '4px', 
              border: '1px solid var(--border)', 
              background: 'white',
              display: 'block'
            }} 
          />
        </div>
      )}
    </Modal>
  );
};

export default ApproveTicketModal;
